import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DateRange } from "react-day-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OpportunityKanban } from "@/components/opportunities/OpportunityKanban";
import { OpportunityTable } from "@/components/opportunities/OpportunityTable";
import { OpportunityDetail } from "@/components/opportunities/OpportunityDetail";
import { OpportunitiesSkeleton } from "@/components/opportunities/OpportunitiesSkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateOpportunityModal } from "@/components/opportunities/CreateOpportunityModal";
import { EditOpportunityModal } from "@/components/opportunities/EditOpportunityModal";
import { CreateStageModal } from "@/components/opportunities/CreateStageModal";
import { OpportunitySourcesModal } from "@/components/opportunities/OpportunitySourcesModal";
import { Opportunity } from "@/services/opportunityService";
import { OpportunityStage } from "@/services/opportunityService";
import { Pipeline } from "@/types/pipeline";
import { 
  Eye, 
  EyeOff, 
  LayoutGrid, 
  LayoutList, 
  List, 
  Plus, 
  Settings, 
  Target, 
  Loader2, 
  AlertCircle,
  ShieldCheck,
  BarChart3,
  Trophy,
  Trash2,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { QuoteViewer } from "@/components/opportunities/QuoteViewer";
import type { QuoteData } from "@/services/opportunityService";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { usePipelinePermissions } from "@/hooks/usePipelinePermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { contactoAPI } from "@/services/contactoService";
import { companyAPI } from "@/services/companyService";
import { opportunityAPI } from "@/services/opportunityService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { currencyAPI } from "@/services/currencyService";
import { usersService } from "@/services/usersService";
import { pipelineAPI } from "@/services/pipelineService";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Opportunities() {
  const { toast } = useToast();
  const auth = useAuth();
  const {
    isAdmin,
    roleId,
    filterPipelinesByPermissions,
    canViewPipeline,
    canCreateOpportunityInPipeline,
    getAccessiblePipelineIds,
  } = usePipelinePermissions();

  const getCompanySlug = () => {
    if (auth.company?.slug) {
      return auth.company.slug;
    }

    if (auth.user?.email) {
      const domain = auth.user.email.split("@")[1]?.split(".")[0];
      return domain || "default";
    }

    return "";
  };

  const companySlug = getCompanySlug();
  const companyName = auth.company?.name || "Empresa";

  const getCurrencySymbol = () => {
    if ((auth.company as any)?.currency) {
      return (auth.company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };
  const currencySymbol = getCurrencySymbol();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [allOpportunities, setAllOpportunities] = useState<Opportunity[]>([]); // This will hold the result from the API
  const [onlyPendingApproval, setOnlyPendingApproval] = useState(false);
  const [kanbanView, setKanbanView] = useState<"kanban" | "table">("kanban");

  const [allStages, setAllStages] = useState<OpportunityStage[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [collapsedPipelines, setCollapsedPipelines] = useState<Set<string>>(
    new Set(),
  );
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [viewingClosedType, setViewingClosedType] = useState<'won' | 'lost' | null>(null);
  const [loading, setLoading] = useState({
    opportunities: false,
    stages: false,
    pipelines: false,
    preload: false,
  });
  const [preloadedData, setPreloadedData] = useState<any>(null);

  const fetchData = useCallback(async (background = false) => {
    if (!companySlug) {
      toast({
        title: "Error",
        description: "No se encontró la empresa",
        variant: "destructive",
      });
      return;
    }

    if (!auth.isAuthenticated) {
      toast({
        title: "No autenticado",
        description: "Debes iniciar sesión para ver oportunidades",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading({
        opportunities: true,
        stages: true,
        pipelines: true,
        preload: false,
      });

      const pipelinesResponse = await pipelineAPI.getPipelines({
        slug: companySlug,
        is_active: true,
      });

      // Obtener los permisos de cada pipeline
      const pipelinesWithPermissions = await Promise.all(
        pipelinesResponse.data.map(async (pipeline) => {
          try {
            const permissionsResponse =
              await pipelineAPI.getPipelinePermissions(
                companySlug,
                pipeline.id,
              );
            return {
              ...pipeline,
              permissions: permissionsResponse.data,
            };
          } catch (error) {
            console.error(
              `Error al obtener permisos del pipeline ${pipeline.id}:`,
              error,
            );
            return {
              ...pipeline,
              permissions: [],
            };
          }
        }),
      );
      const accessiblePipelines = filterPipelinesByPermissions(
        pipelinesWithPermissions,
      );
      setPipelines(accessiblePipelines);

      // Only show error if there ARE pipelines but NONE are accessible
      if (pipelinesResponse.data.length > 0 && accessiblePipelines.length === 0) {
        toast({
          title: "Sin acceso",
          description:
            "No tienes acceso a ningún pipeline. Contacta al administrador.",
          variant: "destructive",
        });
        setLoading({
          opportunities: false,
          stages: false,
          pipelines: false,
          preload: false,
        });
        return;
      }

      // Determina el pipeline para el cual buscar datos. Usa el activo si existe, si no, el predeterminado.
      const pipelineIdToUse =
        activePipelineId ||
        (
          accessiblePipelines.find((p) => p.is_default) ||
          accessiblePipelines[0]
        )?.id;

      if (!activePipelineId && pipelineIdToUse) {
        setActivePipelineId(pipelineIdToUse);
      }

      let stagesData: OpportunityStage[] = [];
      if (pipelineIdToUse) {
        const stagesResponse = await opportunityAPI.getStages({
          slug: companySlug,
          pipeline_id: pipelineIdToUse,
        });
        // Filtrar estrictamente por pipeline_id y eliminar "Cierre Perdido"
        stagesData = stagesResponse.data
          .filter((s: any) => s.pipeline_id === pipelineIdToUse && s.name !== "Cierre Perdido")
          .sort((a: any, b: any) => a.order_position - b.order_position);

        setAllStages(stagesData);
      }

      // 1. Hacemos UNA SOLA llamada para traer las oportunidades según el modo de visibilidad
      const apiParams = {
        slug: companySlug,
        page: 1,
        limit: 1000,
        user_id: isAdmin ? undefined : (auth.user?.id as unknown as string),
        only_hidden: false,
      };

      const oppResponse = await opportunityAPI.getOpportunities({
        slug: companySlug,
        page: 1,
        limit: 1000,
        user_id: isAdmin ? undefined : (auth.user?.id as unknown as string),
        only_hidden: false,
      });

      const accessiblePipelineIds =
        getAccessiblePipelineIds(accessiblePipelines);

      const filterAndSetOpps = (opps: Opportunity[], pipelineId: string) =>
        opps.filter((opp) => {
          if (opp.pipeline_id !== pipelineId) return false;
          if (!accessiblePipelineIds.includes(opp.pipeline_id)) return false;
          return true;
        });

      // 2. Guardamos TODAS las oportunidades en el nuevo estado
      const oppsForLog = filterAndSetOpps(
        oppResponse.data,
        pipelineIdToUse || "",
      );
      setAllOpportunities(oppsForLog);
    } catch (error: any) {
      console.error("Error al cargar datos:", error);
      toast({
        title: "Error al cargar datos",
        description: error.message || "No se pudieron cargar las oportunidades",
        variant: "destructive",
      });
    } finally {
      setLoading({
        opportunities: false,
        stages: false,
        pipelines: false,
        preload: false,
      });
    }
  }, [companySlug, auth, activePipelineId, isAdmin, filterPipelinesByPermissions, getAccessiblePipelineIds]);

  const preloadModalData = useCallback(async () => {
    if (!companySlug || !auth.isAuthenticated) return;

    try {
      setLoading((prev) => ({ ...prev, preload: true }));

      const [
        contactsData,
        companiesResponse,
        stagesResponse,
        sourcesResponse,
        currenciesResponse,
        usersResponse,
      ] = await Promise.all([
        contactoAPI.getContactos(companySlug),
        companyAPI.getCompanies({ slug: companySlug }),
        opportunityAPI.getStages({ slug: companySlug, is_active: true }),
        opportunitySourcesAPI.getSources({
          slug: companySlug,
          is_active: true,
        }),
        currencyAPI.getCurrencies({ slug: companySlug, is_active: true }),
        usersService.getUsers(companySlug),
      ]);

      setPreloadedData({
        contacts: Array.isArray(contactsData) ? contactsData : [],
        companies: companiesResponse.data,
        stages: stagesResponse.data,
        sources: sourcesResponse.data,
        currencies: currenciesResponse.data,
        users: usersResponse.data,
      });
    } catch (error) {
      console.error("Error preloading modal data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, preload: false }));
    }
  }, [companySlug, auth.isAuthenticated]);

  useEffect(() => {
    if (companySlug && auth.isAuthenticated) {
      fetchData();
    }
  }, [fetchData, companySlug, auth.isAuthenticated]);

  useEffect(() => {
    if (companySlug && auth.isAuthenticated) {
      preloadModalData();
    }
  }, [preloadModalData, companySlug, auth.isAuthenticated]);

  // Filtrar solo las versiones más recientes de cada oportunidad para visualización y conteo
  const latestOpportunities = useMemo(() => {
    // Agrupamos por ID de raíz para asegurarnos de mostrar SOLO UNA CARD por cotización.
    // Fallback: Si no hay original_opportunity_id, usamos (Nombre + Cliente) como llave de grupo
    // para agrupar versiones antiguas que no fueron vinculadas correctamente.
    const groups: Record<string, Opportunity> = {};
    
    allOpportunities.forEach(opp => {
      const rootId = opp.original_opportunity_id || opp.id;
      const fallbackKey = `${opp.name}-${opp.client_company_id}`;
      
      // Intentamos encontrar por rootId primero, luego por llave de respaldo
      let groupKey = rootId;
      
      // Si el rootId parece ser una raíz aislada (opp.id === rootId), 
      // verificamos si ya existe alguien con la misma llave de respaldo
      if (opp.id === rootId) {
        const existingWithKey = Object.values(groups).find(g => 
          `${g.name}-${g.client_company_id}` === fallbackKey
        );
        if (existingWithKey) {
          groupKey = existingWithKey.original_opportunity_id || existingWithKey.id;
        }
      }

      const existing = groups[groupKey];
      const currentScore = (opp.version_number || 1) * 1000 + (opp.minor_version || 0);
      
      if (!existing) {
        groups[groupKey] = opp;
      } else {
        const existingScore = (existing.version_number || 1) * 1000 + (existing.minor_version || 0);
        
        // Priorizamos la versión más alta, y si son iguales, la más reciente
        if (currentScore > existingScore) {
          groups[groupKey] = opp;
        } else if (currentScore === existingScore) {
          if (new Date(opp.updated_at) > new Date(existing.updated_at)) {
            groups[groupKey] = opp;
          }
        }
      }
    });

    return Object.values(groups);
  }, [allOpportunities]);

  const filteredOpportunities = useMemo(() => {
    let filtered = latestOpportunities;
    
    if (onlyPendingApproval) {
      filtered = filtered.filter(opp => opp.approval_status === 'pending');
    }
    
    return filtered;
  }, [latestOpportunities, onlyPendingApproval]);

  const handleOpportunityClick = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailModalOpen(true);
  };

  const handleCreateSuccess = () => {
    fetchData();
  };

  const handleOpportunityUpdated = () => {
    setIsDetailModalOpen(false); // Cierra el modal de detalle
    fetchData(true); // Vuelve a cargar los datos en segundo plano
  };

  const handleEditSuccess = () => {
    fetchData();
    toast({
      title: "¡Cotización actualizada!",
      description: "La cotización se ha actualizado correctamente",
      variant: "default",
    });
  };

  const handleOpportunityDeleted = (deletedOpportunityId: string) => {
    setOpportunities((prev) =>
      prev.filter((opp) => opp.id !== deletedOpportunityId),
    );
  };

  const handleStageCreated = () => {
    fetchData();
    preloadModalData();
  };

  const handleSourceCreated = () => {
    preloadModalData();
    toast({
      title: "Fuentes actualizadas",
      description: "Los cambios se reflejarán al crear nuevas oportunidades",
    });
  };

  const handleEditOpportunity = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleUpdateOpportunityStage = async (
    opportunityId: string,
    newStageId: string,
  ) => {
    const newStage = allStages.find((s) => s.id === newStageId);
    if (!newStage) {
      console.error("Could not find the new stage. Refetching data.");
      fetchData();
      return;
    }

    // Solo actualizamos el estado local para reflejar el cambio.
    // La llamada a la API y el manejo del orden visual ya se hacen en OpportunityKanban.
    setOpportunities((prev) =>
      prev.map((opp) =>
        opp.id === opportunityId
          ? { ...opp, stage_id: newStageId, stage: newStage }
          : opp,
      ),
    );
  };

  const handleEditStage = () => {
    setIsStageModalOpen(true);
  };

  const handleManageSources = () => {
    setIsSourcesModalOpen(true);
  };

  const handlePipelineChange = async (pipelineId: string) => {
    if (pipelineId === activePipelineId) {
      return;
    }

    const selectedPipeline = pipelines.find((p) => p.id === pipelineId);

    if (selectedPipeline && !canViewPipeline(selectedPipeline)) {
      toast({
        title: "Sin permisos",
        description: "No tienes permisos para ver este pipeline",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, stages: true, opportunities: true }));
      setActivePipelineId(pipelineId);

      const stagesResponse = await opportunityAPI.getStages({
        slug: companySlug,
        pipeline_id: pipelineId,
      });

      const sortedStages = stagesResponse.data
        .filter((s: any) => s.pipeline_id === pipelineId)
        .sort((a: any, b: any) => a.order_position - b.order_position);
      setAllStages(sortedStages);

      const oppResponse = await opportunityAPI.getOpportunities({
        slug: companySlug,
        page: 1,
        limit: 1000,
        user_id: isAdmin ? undefined : (auth.user?.id as unknown as string),
        exclude_closed: false,
      });

      const accessiblePipelineIds = getAccessiblePipelineIds(pipelines);

      const filterAndSetOpps = (opps: Opportunity[], pid: string) =>
        opps.filter((opp) => {
          if (opp.pipeline_id !== pid) return false;
          if (!accessiblePipelineIds.includes(opp.pipeline_id)) return false;
          return true;
        });

      setAllOpportunities(filterAndSetOpps(oppResponse.data, pipelineId));

      toast({
        title: "Pipeline cambiado",
        description: `Mostrando oportunidades de "${selectedPipeline?.name}"`,
      });
    } catch (error: any) {
      console.error("Error al cargar datos del pipeline:", error);
      toast({
        title: "Error al cargar datos",
        description:
          error.message || "No se pudieron cargar los datos del pipeline",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, stages: false, opportunities: false }));
    }
  };

  const togglePipelineCollapse = (pipelineId: string) => {
    setCollapsedPipelines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pipelineId)) {
        newSet.delete(pipelineId);
      } else {
        newSet.add(pipelineId);
      }
      return newSet;
    });
  };

  const handleOpenCreateModal = () => {
    if (!preloadedData) {
      toast({
        title: "Cargando datos",
        description: "Por favor espera mientras se cargan los datos necesarios",
        variant: "default",
      });
      preloadModalData();
    }
    setIsCreateModalOpen(true);
  };

  const totalValue = latestOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const wonOpportunities = latestOpportunities.filter(
    (opp) => opp.stage?.stage_type === "closed_won",
  );

  const wonValue = wonOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const lostOpportunities = latestOpportunities.filter(
    (opp) => opp.stage?.stage_type === "closed_lost",
  );

  const lostValue = lostOpportunities.reduce(
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const activeOpportunities = latestOpportunities.filter(
    (opp) =>
      opp.stage?.stage_type !== "closed_won" &&
      opp.stage?.stage_type !== "closed_lost",
  );

  const activeValue = filteredOpportunities.reduce(
    // Usar las filtradas para el valor activo
    (sum, opp) => sum + parseFloat(opp.estimated_value || "0"),
    0,
  );

  const avgDealSize =
    latestOpportunities.length > 0 ? totalValue / latestOpportunities.length : 0;
  const winRate =
    wonOpportunities.length + lostOpportunities.length > 0
      ? (wonOpportunities.length /
          (wonOpportunities.length + lostOpportunities.length)) *
        100
      : 0;

  const canViewOpportunities = auth.canManageOpportunities();
  const canCreateOpportunities = auth.can("opportunities.create");
  const canEditOpportunities = auth.can("opportunities.update");
  const canManageStages = auth.can("stages.manage");
  const canManageSources = auth.can("opportunity_sources.manage");

  if (!auth.isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No autenticado</AlertTitle>
          <AlertDescription>
            Debes iniciar sesión para ver las oportunidades.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!canViewOpportunities) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Acceso denegado</AlertTitle>
          <AlertDescription>
            No tienes permisos para ver las oportunidades.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!companySlug) {
    return (
      <div className="flex items-center justify-center h-64">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Empresa no encontrada</AlertTitle>
          <AlertDescription>
            No se pudo identificar la empresa. Contacta al administrador.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading.opportunities || loading.stages || loading.pipelines) {
    return <OpportunitiesSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cotizaciones</h1>
          <p className="text-slate-600">
            Pipeline de ventas y cotizaciones de {companyName}
          </p>
        </div>
      </div>

      {!companySlug && !loading.opportunities && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-amber-700">
            No se ha identificado la empresa. Por favor, inicia sesión.
          </p>
        </div>
      )}

      {companySlug && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-600">
                      Pipeline Activo
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {currencySymbol}
                      {activeValue.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {activeOpportunities.length} cotizaciones activas
                    </p>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 font-bold text-lg opacity-80">
                    {currencySymbol}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-600">
                      Ganadas
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {currencySymbol}
                      {wonValue.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {wonOpportunities.length} cotizaciones ganadas
                    </p>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 text-green-600 font-bold text-lg opacity-80">
                    {currencySymbol}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-600">
                      Tasa de Éxito
                    </p>
                    <p className="text-2xl font-bold text-purple-600">
                      {winRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {lostOpportunities.length} cotizaciones perdidas
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-600 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-slate-600">
                      Valor Promedio
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                      {currencySymbol}
                      {avgDealSize.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {latestOpportunities.length} cotizaciones totales
                    </p>
                  </div>
                  <div className="h-8 w-8 flex items-center justify-center bg-orange-100 rounded-lg text-orange-600 font-bold text-lg">
                    {currencySymbol}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tarjetas de Acceso a Cerradas (Ganadas y Basurero) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <Card 
                className="cursor-pointer hover:shadow-md transition-all border-green-100 bg-green-50/30 group"
                onClick={() => setViewingClosedType('won')}
             >
                <CardContent className="p-4 flex items-center justify-between">
                   <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Trophy className="h-6 w-6" />
                      </div>
                      <div>
                         <p className="text-sm font-semibold text-green-800">Cotizaciones Ganadas</p>
                         <p className="text-xl font-bold text-green-900">
                            {currencySymbol}{wonValue.toLocaleString("en-US")}
                         </p>
                      </div>
                   </div>
                   <div className="flex items-center gap-2 text-green-600 font-medium text-sm bg-white px-3 py-1.5 rounded-lg border border-green-100">
                      <span>{wonOpportunities.length} cotizaciones</span>
                      <ChevronRight className="h-4 w-4" />
                   </div>
                </CardContent>
             </Card>

             <Card 
                className="cursor-pointer hover:shadow-md transition-all border-red-100 bg-red-50/30 group"
                onClick={() => setViewingClosedType('lost')}
             >
                <CardContent className="p-4 flex items-center justify-between">
                   <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Trash2 className="h-6 w-6" />
                      </div>
                      <div>
                         <p className="text-sm font-semibold text-red-800">Basurero (Perdidas)</p>
                         <p className="text-xl font-bold text-red-900">
                            {lostOpportunities.length} cotizaciones
                         </p>
                      </div>
                   </div>
                   <div className="flex items-center gap-2 text-red-600 font-medium text-sm bg-white px-3 py-1.5 rounded-lg border border-red-100">
                      <span>Ver Basurero</span>
                      <ChevronRight className="h-4 w-4" />
                   </div>
                </CardContent>
             </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-4">
              <div className="flex-1">
                {pipelines.length > 1 ? (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                      <Target className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-0.5">
                        Pipeline Activo
                      </p>
                      <Select
                        value={activePipelineId || ""}
                        onValueChange={handlePipelineChange}
                        disabled={loading.opportunities}
                      >
                        <SelectTrigger className="w-auto min-w-[200px] h-auto border-0 p-0 shadow-none bg-transparent text-xl font-bold focus:ring-0 hover:bg-slate-50 px-2 -ml-2 rounded-md">
                          <SelectValue placeholder="Seleccionar pipeline" />
                        </SelectTrigger>
                        <SelectContent>
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              <span className="font-medium">
                                {pipeline.name}
                              </span>
                              {pipeline.is_default && (
                                <span className="ml-2 text-xs text-slate-400">
                                  (Por defecto)
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                      <Target className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">
                        {pipelines[0]?.name || "Pipeline de Cotizaciones"}
                      </CardTitle>
                      <p className="text-sm text-slate-600 mt-1">
                        Arrastra y suelta para cambiar etapas
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-4 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  {canCreateOpportunities && (
                    <Button
                      onClick={handleOpenCreateModal}
                      className="gap-2 bg-black hover:bg-gray-900 text-white border border-gray-800 shadow-sm"
                      disabled={
                        loading.opportunities ||
                        loading.stages ||
                        !companySlug ||
                        loading.preload
                      }
                      size="sm"
                    >
                      {loading.preload ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Nueva Cotización
                    </Button>
                  )}

                  {canManageStages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditStage}
                      className="gap-2 border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                      disabled={loading.stages || !companySlug}
                    >
                      {loading.stages ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Settings className="h-4 w-4 mr-1" />
                      )}
                      Etapas
                    </Button>
                  )}

                  {canManageSources && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManageSources}
                      className="gap-2 border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                      disabled={!companySlug}
                    >
                      <List className="h-4 w-4 mr-1" />
                      Fuentes
                    </Button>
                  )}



                  {/* Filtro Pendientes de Aprobación */}
                  <Button
                    variant={onlyPendingApproval ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOnlyPendingApproval(!onlyPendingApproval)}
                    className={cn(
                      "gap-2",
                      onlyPendingApproval && "bg-amber-600 hover:bg-amber-700 text-white"
                    )}
                    title="Ver solo cotizaciones pendientes de aprobación"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    {onlyPendingApproval ? "Mostrando Pendientes" : "Ver Pendientes"}
                    {latestOpportunities.filter(o => o.approval_status === 'pending').length > 0 && !onlyPendingApproval && (
                      <Badge className="ml-1 px-1.5 h-4 bg-amber-500 text-white border-0">
                        {latestOpportunities.filter(o => o.approval_status === 'pending').length}
                      </Badge>
                    )}
                  </Button>

                  <Tabs
                    value={kanbanView}
                    onValueChange={(v) =>
                      setKanbanView(v as "kanban" | "table")
                    }
                    className="flex-shrink-0"
                  >
                    <TabsList className="bg-slate-100/50 p-1 border border-slate-200">
                      <TabsTrigger
                        value="kanban"
                        title="Vista Kanban"
                        className="data-[state=active]:bg-slate-900 data-[state=active]:text-white h-8 px-3"
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </TabsTrigger>
                      <TabsTrigger
                        value="table"
                        title="Vista Tabla"
                        className="data-[state=active]:bg-slate-900 data-[state=active]:text-white h-8 px-3"
                      >
                        <LayoutList className="h-4 w-4" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {pipelines.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Comienza creando tu primer pipeline
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Necesitas configurar un pipeline para gestionar tus cotizaciones
                  </p>
                  <Button
                    onClick={() => setIsStageModalOpen(true)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Crear mi primer pipeline
                  </Button>
                </div>
              ) : !activePipelineId ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No hay pipeline seleccionado
                  </h3>
                  <p className="text-slate-600">
                    Selecciona un pipeline para ver las cotizaciones
                  </p>
                </div>
              ) : filteredOpportunities.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No hay cotizaciones
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Comienza creando tu primera cotización
                  </p>
                  {canCreateOpportunities && (
                    <Button
                      onClick={handleOpenCreateModal}
                      className="gap-2"
                      disabled={
                        loading.opportunities ||
                        loading.stages ||
                        !companySlug ||
                        loading.preload
                      }
                    >
                      {loading.preload ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Crear primera cotización
                    </Button>
                  )}
                </div>
              ) : (
                <OpportunityKanban
                  slug={companySlug}
                  opportunities={filteredOpportunities}
                  stages={allStages}
                  onOpportunityClick={handleOpportunityClick}
                  onCreateOpportunity={handleOpenCreateModal}
                  onStageEdit={handleEditStage}
                  onStageChange={handleUpdateOpportunityStage}
                  view={kanbanView}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {canCreateOpportunities && (
        <CreateOpportunityModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          slug={companySlug}
          pipelineId={activePipelineId || undefined}
          preloadedData={preloadedData}
        />
      )}

      {canEditOpportunities && (
        <EditOpportunityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          slug={companySlug}
          opportunity={selectedOpportunity}
        />
      )}

      {canManageStages && (
        <CreateStageModal
          isOpen={isStageModalOpen}
          onClose={() => setIsStageModalOpen(false)}
          slug={companySlug}
          pipelineId={activePipelineId || undefined}
          onStageCreated={handleStageCreated}
          onStageUpdated={handleStageCreated}
          onStageDeleted={handleStageCreated}
        />
      )}

      <OpportunitySourcesModal
        isOpen={isSourcesModalOpen}
        onClose={() => setIsSourcesModalOpen(false)}
        slug={companySlug}
        onSourceCreated={handleSourceCreated}
      />

      <OpportunityDetail
        opportunityId={selectedOpportunity?.id || null}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        slug={companySlug}
        onEdit={handleEditOpportunity}
        onOpportunityUpdated={handleOpportunityUpdated}
        onOpportunityDeleted={handleOpportunityDeleted}
      />

      {/* Modal para ver Ganadas/Perdidas */}
      <Dialog 
        open={viewingClosedType !== null} 
        onOpenChange={(open) => !open && setViewingClosedType(null)}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-4">
           <DialogHeader className="mb-2">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                 {viewingClosedType === 'won' ? (
                    <>
                       <Trophy className="h-6 w-6 text-green-600" />
                       Historial de Cotizaciones Ganadas
                    </>
                 ) : (
                    <>
                       <Trash2 className="h-6 w-6 text-red-600" />
                       Basurero de Cotizaciones Perdidas
                    </>
                 )}
              </DialogTitle>
           </DialogHeader>
           
           <div className="mt-4 border-t pt-4">
               <OpportunityTable
                  slug={companySlug}
                  opportunities={viewingClosedType === 'won' ? wonOpportunities : lostOpportunities}
                  stages={allStages}
                  onView={(opp) => {
                     setViewingClosedType(null);
                     handleOpportunityClick(opp);
                  }}
               />
           </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
